/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { IModuleProps, getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';

import { IContractsAccountPageData } from './contracts-account-page.data';
import { IContractsAccountPageProps } from './contracts-account-page.props.autogenerated';
import LinksComponent from './components/contracts-account-page-links';
import { IContractListEntity_V2 } from '../../actions/DataServiceEntities.g';

export interface IContractsAccountPageViewProps extends IContractsAccountPageProps<IContractsAccountPageData> {
    contractsHeading: React.ReactElement;
    contractsWrapper: IModuleProps;
    contractsTable: React.ReactElement;
    contractsLinks: React.ReactElement;
}

/**
 *
 * ContractsAccountPage component
 * @extends {React.PureComponent<IContractsAccountPageProps<IContractsAccountPageData>>}
 */
class ContractsAccountPage extends React.PureComponent<IContractsAccountPageProps<IContractsAccountPageData>> {
    private readonly telemetryContent: ITelemetryContent;
    constructor(props: IContractsAccountPageProps<IContractsAccountPageData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        this._renderHeading = this._renderHeading.bind(this);
        this._getContractsRowData = this._getContractsRowData.bind(this);
        this._handleHeadingChange = this._handleHeadingChange.bind(this);
        this._renderTable = this._renderTable.bind(this);
        this._generateRow = this._generateRow.bind(this);
    }

    private _renderHeading(): React.ReactElement {
        const {
            config: { heading },
            context: { request }
        } = this.props;

        if (!heading || !heading.text) {
            return <></>;
        }

        return (
            <Msdyn365.Text
                className='eve-contracts-tile__heading'
                tag={heading.tag || 'h2'}
                text={heading.text}
                editProps={{ onEdit: this._handleHeadingChange, requestContext: request }}
            />
        );
    }

    private _getContractsRowData(): IContractListEntity_V2[] {
        if (!this.props.data.contractList.result) {
            return [];
        }

        if (this.props.data.contractList.result && this.props.data.contractList.result.length > 0) {
            return this.props.data.contractList.result;
        }

        return [];
    }

    private _handleHeadingChange = (event: Msdyn365.ContentEditableEvent): string => (this.props.config.heading.text = event.target.value);

    private _renderTable(): React.ReactElement {
        const {
            resources: { manufacturerLabel, gdnContractIdLabel, laborRateLabel, contractDescLabel, noContractsFoundText }
        } = this.props;

        const headingRow = [manufacturerLabel, gdnContractIdLabel, contractDescLabel, laborRateLabel];
        const contractsRowData = this._getContractsRowData();

        return (
            <div className='contracts-table-wrapper'>
                <ul className='contracts-table'>
                    <li className='contracts-table-header'>
                        {headingRow.map((heading, i) => (
                            <div key={heading} className={`contracts-col contracts-col-${i + 1}`}>
                                {heading}
                            </div>
                        ))}
                    </li>
                    {contractsRowData.length > 0 ? (
                        contractsRowData.map(contract => (
                            <li
                                className='contracts-table-row'
                                key={`${contract.ManufacturerCode || ''}-${contract.ContractCode || ''}-${contract.ContractNumber || ''}`}
                            >
                                {this._generateRow(contract)}
                            </li>
                        ))
                    ) : (
                        <li className='contracts-table-row contracts-table-row-empty' key='empty'>
                            {this._generateEmptyRow(noContractsFoundText)}
                        </li>
                    )}
                </ul>
            </div>
        );
    }

    private _generateRow(contractData: IContractListEntity_V2): React.ReactElement {
        const {
            resources: { manufacturerLabel, gdnContractIdLabel, laborRateLabel, contractDescLabel }
        } = this.props;

        const { ManufacturerCode, ManufacturerDescription, ContractCode, ContractLaborRate, Description } = contractData;

        return (
            <>
                <div className='contracts-col contracts-col-1' data-title={manufacturerLabel}>
                    <p className='contracts-table__manufacturer-code'>{ManufacturerCode}</p>
                    {ManufacturerDescription && <p className='contracts-table__manufacturer-name'>{ManufacturerDescription}</p>}
                </div>

                <div className='contracts-col contracts-col-2' data-title={gdnContractIdLabel}>
                    <p>{ContractCode}</p>
                </div>

                <div className='contracts-col contracts-col-3' data-title={contractDescLabel}>
                    <p>{Description}</p>
                </div>

                <div className='contracts-col contracts-col-4' data-title={laborRateLabel}>
                    <p>{this.props.context.cultureFormatter.formatCurrency(ContractLaborRate)}</p>
                </div>
            </>
        );
    }

    private _generateEmptyRow(noContractsFoundText: string) {
        return (
            <div className='contracts-table__empty-message'>
                <p>{noContractsFoundText}</p>
            </div>
        );
    }

    private _renderLink(): React.ReactElement {
        const {
            config: { links },
            context: { request }
        } = this.props;

        if (!links || links.length < 1) {
            return <></>;
        }

        return (
            <LinksComponent
                {...{ links, onTextChange: this._handleLinkTextChange, requestContext: request, telemetryContent: this.telemetryContent }}
            />
        );
    }

    private _handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        const { links } = this.props.config;
        if (links && links[linkIndex]) {
            links[linkIndex].linkText = event.target.value;
        }
    };

    public render(): JSX.Element | null {
        const viewProps = {
            ...this.props,
            contractsHeading: this._renderHeading(),
            contractsWrapper: {
                className: classNames('eve-contracts-tile', 'contracts-container'),
                tag: 'div',
                moduleProps: this.props
            },
            contractsTable: this._renderTable(),
            contractsLinks: this._renderLink()
        };

        return this.props.renderView(viewProps);
    }
}

export default ContractsAccountPage;
